import { ME_EVENT_TABS, type Locale, type MeEventTab } from '@spot/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { TAB_PATHS } from '../../app/paths';
import { Chip } from '../../components/ui/chip';
import { useSession } from '../auth/session-gate';
import { DEFAULT_CITY } from '../map/city';
import { mapMemory } from '../map/memory';
import { EmptyState } from '../../components/ui/empty-state';
import { EventList } from './event-list';
import { useMyEvents } from './queries';

let rememberedTab: MeEventTab = 'upcoming';

/** Мої: Майбутні / Створені / Минулі / Збережені, each with its own empty state. */
export function MineTab({
	locale,
	initialTab,
}: {
	locale: Locale;
	/** From a link (e.g. the profile's «Збережені»); otherwise the last tab used. */
	initialTab?: MeEventTab | undefined;
}) {
	const { t } = useTranslation();
	const { me } = useSession();
	const navigate = useNavigate();
	const [tab, setTabState] = useState<MeEventTab>(initialTab ?? rememberedTab);
	const setTab = (next: MeEventTab) => {
		rememberedTab = next;
		setTabState(next);
	};
	const query = useMyEvents(tab);

	const toMap = { label: t('activities.find'), onClick: () => void navigate(TAB_PATHS.map) };
	const toCreate = {
		label: t('activities.create'),
		onClick: () => void navigate(TAB_PATHS.create),
	};
	const empty: Record<MeEventTab, { emoji: string; text: string; action: typeof toMap }> = {
		upcoming: { emoji: '🗓️', text: t('activities.empty.upcoming'), action: toMap },
		created: { emoji: '✨', text: t('activities.empty.created'), action: toCreate },
		past: { emoji: '📸', text: t('activities.empty.past'), action: toMap },
		saved: { emoji: '🔖', text: t('activities.empty.saved'), action: toMap },
	};

	return (
		<>
			{me.botAccessAsked && !me.botCanMessage && (
				<p className="text-caption text-text-muted bg-surface rounded-card px-3 py-2">
					{t('apply.decisions_in_app')}
				</p>
			)}
			<div
				role="group"
				aria-label={t('activities.mine')}
				className="-mx-4 flex gap-2 overflow-x-auto px-4 py-2 [scrollbar-width:none]"
			>
				{ME_EVENT_TABS.map((value) => (
					<Chip
						key={value}
						selected={tab === value}
						onClick={() => {
							setTab(value);
						}}
					>
						{t(`activities.tabs.${value}`)}
					</Chip>
				))}
			</div>
			<EventList
				query={query}
				timeZone={DEFAULT_CITY.timezone}
				locale={locale}
				viewer={mapMemory.position}
				empty={
					<EmptyState emoji={empty[tab].emoji} text={empty[tab].text} action={empty[tab].action} />
				}
			/>
		</>
	);
}
